import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { statsData, platformFeatures } from '@/mocks/homeData';

const milestones = [
  { year: '2015', title: '平台成立', description: '开云体育在线正式上线运营，致力于打造一流的体育娱乐平台' },
  { year: '2017', title: '用户突破100万', description: '凭借优质服务和丰富赛事内容，注册用户数突破百万大关' },
  { year: '2019', title: '国际化拓展', description: '业务范围扩展至东南亚、欧洲等多个国家和地区' },
  { year: '2021', title: '技术全面升级', description: '全面升级平台技术架构，引入AI智能赔率系统和区块链加密技术' },
  { year: '2023', title: '用户突破500万', description: '全球注册用户突破500万，成为行业领先的体育娱乐平台' },
  { year: '2026', title: '持续创新', description: '持续优化产品体验，拓展电竞赛事等新兴领域，引领行业发展' },
];

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const hpField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (hpField && hpField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (hpField) formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9rffsue3oq4jqumu0mg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-950 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-1.5 text-xs mb-4" aria-label="面包屑导航">
              <Link to="/" className="text-background-400 hover:text-background-200 transition-colors duration-200 whitespace-nowrap">首页</Link>
              <span className="text-background-500">/</span>
              <span className="text-background-200 whitespace-nowrap">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50">关于开云体育</h1>
            <p className="text-sm text-background-400 mt-2 max-w-[600px]">了解开云体育在线的品牌故事、信誉保障和联系方式</p>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
            <div className="flex items-center gap-1 py-3">
              {([
                { key: 'intro' as const, label: '品牌介绍' },
                { key: 'reputation' as const, label: '信誉保障' },
                { key: 'contact' as const, label: '联系我们' },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {activeTab === 'intro' && (
          <section className="py-10 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-[1000px] mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
                  <div className="space-y-4">
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      开云体育在线成立于2015年，是一家专注于全球体育赛事服务的综合性在线平台。我们秉承"诚信为本、用户至上"的经营理念，致力于为广大体育爱好者提供安全、专业、便捷的体育赛事服务。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      经过多年发展，开云体育在线已成为行业领先的体育娱乐品牌。平台覆盖足球、篮球、网球、电竞等超过30种热门体育项目，服务全球超过500万注册用户。我们与多家国际顶级体育数据供应商深度合作，确保赔率数据的实时性和准确性。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      技术驱动是开云体育在线的核心竞争力。平台采用分布式系统架构和AI智能算法，为用户提供毫秒级的数据更新和流畅的使用体验。同时，我们建立了完善的安全风控体系，运用银行级加密技术和多重身份验证机制，全方位保障用户的信息和资金安全。
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden h-64 lg:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20corporate%20headquarters%20building%20with%20glass%20facade%20at%20sunset%2C%20professional%20business%20atmosphere%2C%20warm%20golden%20lighting%20reflecting%20on%20windows%2C%20clean%20minimalist%20architectural%20design%2C%20premium%20corporate%20photography%20style%2C%20professional%20and%20trustworthy%20aesthetic&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="开云体育在线品牌形象"
                    title="开云体育在线 - 品牌总部"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((m, idx) => (
                    <div key={m.year} className={`flex items-center gap-4 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                        <div className="bg-background-100 rounded-lg p-4 border border-background-200/60 inline-block">
                          <h4 className="text-sm font-semibold text-foreground-900 mb-1">{m.title}</h4>
                          <p className="text-xs text-foreground-600">{m.description}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 relative z-10">
                        <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
                          <span className="text-xs font-bold text-background-50">{m.year}</span>
                        </div>
                      </div>
                      <div className="flex-1 md:hidden">
                        <div className="bg-background-100 rounded-lg p-4 border border-background-200/60">
                          <h4 className="text-sm font-semibold text-foreground-900 mb-1">{m.title}</h4>
                          <p className="text-xs text-foreground-600">{m.description}</p>
                        </div>
                      </div>
                      <div className={`flex-1 ${idx % 2 !== 0 ? 'md:text-left' : 'md:text-right'} hidden md:block`}>
                        {idx % 2 !== 0 && (
                          <div className="bg-background-100 rounded-lg p-4 border border-background-200/60 inline-block">
                            <h4 className="text-sm font-semibold text-foreground-900 mb-1">{m.title}</h4>
                            <p className="text-xs text-foreground-600">{m.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'reputation' && (
          <section className="py-10 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-[1000px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">信誉保障</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
                {[
                  { icon: 'ri-shield-check-line', title: '合法运营', desc: '持有国际合法运营牌照，严格遵守所在地法律法规，定期接受第三方审计' },
                  { icon: 'ri-lock-line', title: '数据加密', desc: '采用银行级256位SSL加密技术，所有用户数据传输和存储均经过多层加密保护' },
                  { icon: 'ri-funds-line', title: '资金安全', desc: '用户资金独立托管，与平台运营资金严格分离，全程可追溯可审计' },
                  { icon: 'ri-shield-user-line', title: '隐私保护', desc: '严格执行隐私保护政策，绝不向第三方泄露用户个人信息和交易数据' },
                  { icon: 'ri-scales-line', title: '公平公正', desc: '采用国际认证的随机算法和赔率计算系统，确保每一场比赛的公平性' },
                  { icon: 'ri-customer-service-2-line', title: '优质服务', desc: '7×24小时专业客服团队在线，3分钟内快速响应，解决您的所有问题' },
                ].map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-3">
                      <i className={`${item.icon} text-accent-700 text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {statsData.map((stat) => (
                  <div key={stat.label} className="text-center bg-background-100 rounded-lg p-5 border border-background-200/60">
                    <div className="text-2xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-xs text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'contact' && (
          <section className="py-10 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-[1000px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">联系我们</h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-customer-service-2-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">在线客服</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-1">7×24小时在线，专业客服团队为您服务</p>
                      <p className="text-xs text-primary-500 font-medium">响应时间：3分钟以内</p>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-accent-700 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服邮箱</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-1">发送邮件至官方客服邮箱</p>
                      <p className="text-xs text-primary-500 font-medium">service@kaiyunsport.com</p>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                      <i className="ri-phone-line text-secondary-700 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-1">电话客服工作时间为每日9:00-24:00</p>
                      <p className="text-xs text-primary-500 font-medium">400-888-9999</p>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-5 border border-background-200/60 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 flex-shrink-0">
                      <i className="ri-map-pin-line text-foreground-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">开云体育在线总部</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6 border border-background-200/60">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-3" data-readdy-form="d9rffsue3oq4jqumu0mg">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/60 rounded-md focus:outline-none focus:border-primary-300 text-foreground-800 placeholder:text-foreground-400"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/60 rounded-md focus:outline-none focus:border-primary-300 text-foreground-800 placeholder:text-foreground-400"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言内容（500字以内）"
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/60 rounded-md focus:outline-none focus:border-primary-300 text-foreground-800 placeholder:text-foreground-400 resize-none"
                        required
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-hp"
                    />
                    {formStatus === 'success' && (
                      <p className="text-xs text-green-600 text-center py-1 bg-green-50 rounded">消息已发送成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-primary-600 text-center py-1 bg-primary-50 rounded">{formError || '提交失败，请稍后重试'}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="py-10 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-[1000px] mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">平台优势</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {platformFeatures.map((feature) => (
                <div key={feature.title} className="bg-background-50 rounded-lg p-5 text-center border border-background-200/60">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${feature.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">{feature.title}</h4>
                  <p className="text-xs text-foreground-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于开云体育在线',
            description: '开云体育在线 - 官方正品体育平台入口，了解品牌故事、信誉保障和联系方式',
            publisher: { '@type': 'Organization', name: '开云体育在线' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunsport.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
    </>
  );
}